import { Outlet } from 'react-router';
import { ToolBar } from './tab_bar.component';
import { NavigationBar } from './navigation_bar.component';
import {
	Box,
	BreakPoints,
	Button,
	DialogModal,
	Flex,
	TextLink,
	Tooltip,
	useBreakpoint,
	Text,
	InputSelect,
	Flag,
	Divider,
} from '@cellula/react';
import { Logo } from './logo.component';
import { primitiveColorTokens, tokens } from '@cellula/react-theme-patient';
import styled from 'styled-components';
import { Nav } from './nav.components';
import { TopBar } from './top_bar.component';
import { Injectable, useResolveController, ControllerProtocol, useResolve } from '@visiba/instantiation';
import { computed, signal, useSignal } from '@preact/signals-react';
import { AnimatePresence, motion } from 'framer-motion';
import { Title } from './title.component';
import * as ScrollArea from '@radix-ui/react-scroll-area';
import { Illustration } from './illustration/illustration.component';
import { Link } from 'react-router-dom';
import { withTranslation } from '@application/i18n/with_translation';
import { UniversalConfig } from '@application/universal_config.service';
import { Auth } from '@feature/auth/auth.component';
import { useUser } from '@application/authentication/use_user';
import { applicationLocalStorage } from '@application/application_storage_local';
import { DEFAULT_LAYOUT_VARIANT } from '../constants';
import { ConsentTrigger } from './consent/consent_trigger.component';
import { QrCode } from './qr_code.component';
import { GeneralApiService } from '@api_patient/general_api.service';
import { AuthService } from '@application/authentication/auth.service';
import { UserSettingsService } from '@feature/settings/user_settings.service';
import { I18nAndL10n } from '@application/i18n/mod';
import { localeToCountry } from '@module/locale_to_country_code';
import { SignOutButton } from './sign_out_button.component';
import { AndroidBadge, IOSBadge } from './app_badge.component';

interface OutletPageProps {
	padding?: string;
}
export function OutletPage({ padding }: OutletPageProps) {
	return (
		<Flex
			grow={1}
			style={{
				height: '100%',
				minHeight: '100%',
			}}
		>
			<TopBar.Root>
				<TopBar.Outlet />
				<StyledContainer padding={padding}>
					<Outlet />
				</StyledContainer>
			</TopBar.Root>
		</Flex>
	);
}

export function OutletMain() {
	const breakpoint = useBreakpoint();

	if (breakpoint === BreakPoints.Large) {
		return <GoldenLayout key='golden' />;
	}

	return (
		<>
			<ToolBar />
			<Outlet />
		</>
	);
}

export function OutletPageNavigation() {
	const breakpoint = useBreakpoint();

	return (
		<>
			{breakpoint !== BreakPoints.Large ? <NavigationBar /> : null}
			<Outlet />
		</>
	);
}

const StyledHeaderContainer = styled.header`
	position: fixed;
	top: 0;
	left: 0;
	width: 250px;
	display: flex;
	flex-direction: column;
	flex-shrink: 0;
	padding: ${tokens.size.x2};
	border-right: 1px solid ${primitiveColorTokens.neutral[200]};
	gap: ${tokens.size.x4};
	height: 100%;

	&::before {
		display: block;
		content: '';
		display: flex;
		position: absolute;
		inset: 0;
		background: linear-gradient(200deg, transparent 26%, ${primitiveColorTokens.primary['300']});
		opacity: 0.5;
		pointer-events: none;
		z-index: 0;
	}
`;

const StyledHeaderContainerDisplacement = styled.div`
	width: 250px;
	max-height: 100vh;
`;

const StyledContainer = styled.div<{ padding?: string }>`
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	padding: ${(props) => props.padding || tokens.spacing.x2};

	${tokens.breakpointQuery.small} {
		padding: ${(props) => props.padding || `${tokens.spacing.x3} ${tokens.spacing.x4} ${tokens.spacing.x4}`};
		max-width: 580px;
		width: 100%;
		margin: 0 auto;
	}
`;

const StyledWebsiteMain = styled.main`
	display: flex;
	flex-direction: column;
	min-height: calc(100vh - 150px);
`;

const StyledHeaderShell3 = styled.header`
	position: sticky;
	top: 0;
	box-shadow: ${tokens.elevation.skim};
	border-bottom: 1px solid ${tokens.color.border};
	z-index: 1;

	&:after {
		content: '';
		position: absolute;
		inset: 0;
		z-index: -1;

		--extended-by: 100px;
		bottom: calc(-1 * var(--extended-by));
		top: -1px;

		--cutoff: calc(100% - var(--extended-by));

		mask-image: linear-gradient(to bottom, black 0, black var(--cutoff), transparent var(--cutoff));
		-webkit-mask-image: linear-gradient(to bottom, black 0, black var(--cutoff), transparent var(--cutoff));

		--blur: 15px;
		backdrop-filter: blur(var(--blur));
		-webkit-backdrop-filter: blur(var(--blur));

		background: linear-gradient(180deg, #fff 15%, transparent 100%);

		user-select: none;
		pointer-events: none;
	}
`;

const VARIANT = applicationLocalStorage.get('shell-variant') ?? DEFAULT_LAYOUT_VARIANT;

function GoldenLayout() {
	const user = useUser();

	if (VARIANT === 'shell-3') {
		return (
			<Flex
				style={{
					position: 'relative',
				}}
			>
				<StyledHeaderShell3>
					<Flex
						direction='row'
						justify='space-between'
						style={{
							padding: `${tokens.size.x2}`,
						}}
					>
						<Flex direction='row' align='center' gap={tokens.size.x4}>
							<Logo />

							<Nav.Content data-variant={VARIANT}>
								<Nav.ItemHome />
								<Nav.ItemBrowse />
								<Nav.ItemVisits />
							</Nav.Content>
						</Flex>

						<Flex direction='row' align='center' gap={tokens.size.x2}>
							{user != null ? <Nav.ItemUser /> : null}
							{user != null ? (
								<SignOutButton />
							) : (
								<Auth.SignInSheet
									returnUrl='/'
									trigger={<Button variant='primary'>{withTranslation('Common.Button.SignIn')}</Button>}
								/>
							)}
						</Flex>
					</Flex>
				</StyledHeaderShell3>

				<StyledWebsiteMain>
					<Outlet />
				</StyledWebsiteMain>

				<Footer />
			</Flex>
		);
	}

	if (VARIANT === 'shell-2') {
		return (
			<Flex direction='row' grow='1'>
				<StyledHeaderContainerDisplacement>
					<StyledHeaderContainer>
						<Logo />

						<Nav.Root>
							<Flex gap={tokens.size.x1}>
								<Nav.Content>
									<Nav.ItemHome />
									<Nav.ItemVisits />
									<Nav.ItemBrowse />
								</Nav.Content>
							</Flex>

							<Flex grow='1' justify='end'>
								<Nav.Item icon='menu' label='Mer' to='/settings' onBrand />
							</Flex>
						</Nav.Root>
					</StyledHeaderContainer>
				</StyledHeaderContainerDisplacement>

				<Flex grow='1' as='main'>
					<Outlet />
				</Flex>
			</Flex>
		);
	}

	return (
		<StyledAltLayout key='styledAltlayout'>
			<Floaters key='floaters' />
		</StyledAltLayout>
	);
}

function Footer() {
	const universalConfig = useResolve(UniversalConfig);
	const generalApiService = useResolve(GeneralApiService);
	const authService = useResolve(AuthService);
	const userSettingsService = useResolve(UserSettingsService);

	function handleOnValueChange(languageCode: string) {
		I18nAndL10n.updateLangCode(languageCode);

		localStorage.setItem('languageCode', languageCode);
		userSettingsService.updateSettings({
			selectedLanguage: I18nAndL10n.convertExternalLocaleToAvailableLocale(languageCode),
		});
		if (authService.isSignedIn()) {
			generalApiService.setlanguage(languageCode).then(() => {
				window.location.reload();
			});
		} else {
			window.location.reload();
		}
	}

	return (
		<Flex
			as='footer'
			style={{
				borderTop: `1px solid ${tokens.color.border}`,
				background: tokens.color.bg.surfaceBrand,
				padding: `${tokens.spacing.x3}`,
			}}
		>
			<Flex
				direction='row'
				style={{
					margin: '0 auto',
					width: '100%',
					maxWidth: '1200px',
					padding: `${tokens.spacing.x3} ${tokens.spacing.x3}`,
				}}
			>
				<Flex basis='33%' align='start' grow={1}>
					<Flex align='start' justify='start' fluid='directional'>
						<InputSelect.Root
							value={I18nAndL10n.convertExternalLocaleToAvailableLocale(universalConfig.selectedLanguageCode.value)}
							label={withTranslation('PW.Common.ChooseLanguage')}
							placeholder={withTranslation('Common.ChangeLanguage')}
							onValueChange={handleOnValueChange}
						>
							{universalConfig.availableLanguages.map((x) => (
								<InputSelect.Item key={x} value={I18nAndL10n.convertExternalLocaleToAvailableLocale(x)}>
									<Flex direction='row' gap={tokens.size.x1} align='center'>
										<Flag flag={localeToCountry(x)} size='medium' /> {x}
									</Flex>
								</InputSelect.Item>
							))}
						</InputSelect.Root>
					</Flex>
				</Flex>
				<Flex basis='33%' align='center' grow={1}>
					<Flex align='start' justify='end' fluid='directional' gap={tokens.size.x1_5}>
						<TextLink size='small' href={universalConfig.legal.pp} target='_blank' icon='linkOut' iconPosition='trailing'>
							{withTranslation('PW.Component.Footer.PP')}
						</TextLink>

						<TextLink size='small' href={universalConfig.legal.tos} target='_blank' icon='linkOut' iconPosition='trailing'>
							{withTranslation('PW.Component.Footer.ToS')}
						</TextLink>

						<TextLink
							size='small'
							href={universalConfig.alias + '/cookie-policy'}
							target='_blank'
							icon='linkOut'
							iconPosition='trailing'
						>
							{withTranslation('Component.Footer.CookiePolicy')}
						</TextLink>

						<ConsentTrigger />
					</Flex>
				</Flex>
				<Flex basis='33%' align='end' grow={1}>
					<DialogModal.Root
						trigger={
							<Button variant='secondary' size='xSmall'>
								{withTranslation('PW.Component.Footer.DownloadApp.Title')}
							</Button>
						}
					>
						<DialogModal.Title>
							{withTranslation('Component.AppQRDownload.Title', {
								appName: universalConfig.alias,
							})}
						</DialogModal.Title>
						<DialogModal.Content>
							<Flex
								gap={tokens.size.x4}
								style={{
									margin: `${tokens.spacing.x3} 0`,
								}}
							>
								<Flex align='center' gap={tokens.size.x2}>
									<Text.p variant='bodyRegular' color={tokens.color.content.default}>
										{withTranslation('Component.AppQRDownload.Scan')}
									</Text.p>
									<Flex align='center'>
										<QrCode qrCode={location.origin + universalConfig.alias + '/app-download'} />
									</Flex>
								</Flex>

								<Divider decorative offset={tokens.size.x2} />

								<Flex fluid='horizontal' align='center' gap={tokens.size.x2}>
									<Text.h3 variant='bodyRegular'>{withTranslation('Component.AppQRDownload.Stores')}</Text.h3>

									<Flex direction='row' gap={tokens.size.x0_25} justify='center'>
										{universalConfig.appConfig?.androidPackageId != null ? (
											<Link
												target='__blank'
												to={`https://play.app.goo.gl/?link=https://play.google.com/store/apps/details?id=${universalConfig.appConfig.androidPackageId}`}
											>
												<AndroidBadge />
											</Link>
										) : null}

										{universalConfig.appConfig?.appStoreId != null ? (
											<Link target='__blank' to={`https://itunes.apple.com/se/app/id${universalConfig.appConfig.appStoreId}`}>
												<IOSBadge />
											</Link>
										) : null}
									</Flex>
								</Flex>
							</Flex>
						</DialogModal.Content>
					</DialogModal.Root>
				</Flex>
			</Flex>
		</Flex>
	);
}

const StyledAltLayout = styled.div`
	display: flex;
	flex-grow: 1;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	background: #fafbfb;
	position: relative;

	--shadow-color: 248deg 9% 69%;
	--shadow-color: 218deg 19% 71%;
	--shadow-color: 212deg 25% 65%;
	--shadow-elevation-low:
		0px 0.3px 0.4px hsl(var(--shadow-color) / 0.16), 0px 0.4px 0.5px -0.8px hsl(var(--shadow-color) / 0.21),
		0px 0.9px 1.1px -1.6px hsl(var(--shadow-color) / 0.26);
	--shadow-elevation-medium:
		0px 0.3px 0.4px hsl(var(--shadow-color) / 0.16), 0px 0.9px 1.1px -0.5px hsl(var(--shadow-color) / 0.21),
		0px 2.1px 2.6px -1.1px hsl(var(--shadow-color) / 0.25), 0.1px 4.7px 5.9px -1.6px hsl(var(--shadow-color) / 0.29);
	--shadow-elevation-high:
		0px 0.3px 0.4px hsl(var(--shadow-color) / 0.17), 0px 1.7px 2.1px -0.3px hsl(var(--shadow-color) / 0.2),
		0px 3.1px 3.9px -0.5px hsl(var(--shadow-color) / 0.23), 0.1px 5.1px 6.4px -0.8px hsl(var(--shadow-color) / 0.25),
		0.1px 8.1px 10.1px -1.1px hsl(var(--shadow-color) / 0.28), 0.2px 12.8px 16px -1.4px hsl(var(--shadow-color) / 0.3),
		0.3px 19.5px 24.4px -1.6px hsl(var(--shadow-color) / 0.33);

	&::before {
		display: block;
		content: '';
		display: flex;
		position: absolute;
		inset: 0;
		background: linear-gradient(150deg, transparent 26%, ${primitiveColorTokens.primary['300']});
		pointer-events: none;
		z-index: 0;
		opacity: 0.9;
	}

	&.stacked {
		scale: 0.9;
		border-radius: 32px;
		overflow: hidden;
		opacity: 0.5;
	}
`;

const StyledHeader = styled(motion.header)`
	width: 250px;
	height: 100%;
	display: flex;
	flex-direction: column;
	background: #fcfcfc;
	border-radius: 16px;
	box-shadow: var(--shadow-elevation-medium);
	overflow: hidden;
	padding: 8px 8px 8px;
	position: relative;
	z-index: 0;
	gap: 16px;

	&::before {
		display: block;
		content: '';
		display: flex;
		position: absolute;
		inset: 0;
		background: linear-gradient(200deg, transparent 26%, ${primitiveColorTokens.primary['300']});
		opacity: 0.5;
		pointer-events: none;
		z-index: 0;
		opacity: 0.5;
	}
`;

const StyledNav = styled(motion.nav)`
	position: relative;
	width: 350px;
	height: 100%;
	display: flex;
	flex-direction: column;
	background: #fcfcfc;
	border-radius: 16px;
	box-shadow: var(--shadow-elevation-medium);
	overflow: hidden;
	z-index: 0;
`;

const StyledMain = styled(motion.main)`
	position: relative;
	display: flex;
	flex-direction: column;
	background: #fcfcfc;
	width: 924px;
	height: 100%;
	border-radius: 16px;
	box-shadow: var(--shadow-elevation-medium);
	overflow-y: auto;
	z-index: 0;
`;

const StyledFooter = styled.footer`
	display: flex;
	align-items: center;
	justify-content: center;
	position: fixed;
	left: 0;
	bottom: ${tokens.size.x4};
	width: 100%;
	margin: 0 auto;
	padding: 0 ${tokens.size.x2};
	gap: ${tokens.size.x5};
`;

function Floaters() {
	const controller = useResolveController(FloatersController);
	const universalConfig = useResolve(UniversalConfig);

	const x = useSignal(false);

	return (
		<motion.div
			layout
			className='stacked'
			style={{
				display: 'flex',
				flexDirection: 'row',
				gap: '12px',
				justifyContent: 'center',
				position: 'fixed',
				top: controller.pos.value.y,
				left: controller.pos.value.x,
				width: controller.pos.value.width,
				height: controller.pos.value.height,
			}}
		>
			<StyledHeader layout>
				<Nav.Root>
					<Logo />
					<Flex>
						<Nav.Content>
							<Nav.ItemHome />
							<Nav.ItemVisits />
							<Nav.ItemBrowse />
							<Nav.ItemUser />
						</Nav.Content>
					</Flex>

					<Flex grow='1' justify='end'>
						<Nav.Item icon='menu' label={withTranslation('Component.Nav.More')} to='/settings' onBrand />
					</Flex>
				</Nav.Root>
			</StyledHeader>

			<AnimatePresence mode='popLayout' initial={false}>
				{x.value ? (
					<StyledNav
						layout
						transition={{
							duration: 0.4,
							type: 'tween',
						}}
						initial={{ translateX: 70, opacity: 0, rotateY: 90, scale: 0.97 }}
						animate={{ translateX: 0, opacity: 1, rotateY: 0, scale: 1 }}
						exit={{ translateX: 70, opacity: 0, rotateY: -90, scale: 0.97 }}
					>
						<Flex
							as={ScrollArea.Root}
							style={{
								width: '100%',
								height: '100%',
								overflow: 'hidden',
							}}
						>
							<ScrollArea.Viewport
								style={{
									width: '100%',
									height: '100%',
									padding: `${tokens.spacing.x4} ${tokens.spacing.x2}`,
								}}
							>
								<Title title='Inställningar' />
							</ScrollArea.Viewport>
						</Flex>
					</StyledNav>
				) : null}
			</AnimatePresence>

			<StyledMain layout>
				<Outlet />
			</StyledMain>

			<StyledFooter>
				<TextLink size='small' as={Link} to='/my-account/about-us'>
					{withTranslation('Component.Footer.AboutUs')}
				</TextLink>

				<TextLink size='small' href={universalConfig.alias + '/privacy-policy'} target='_blank' icon='linkOut' iconPosition='trailing'>
					{withTranslation('Component.Footer.PP')}
				</TextLink>

				<TextLink
					size='small'
					href={universalConfig.alias + '/terms-of-service'}
					target='_blank'
					icon='linkOut'
					iconPosition='trailing'
				>
					{withTranslation('Component.Footer.ToS')}
				</TextLink>

				<TextLink size='small' href={universalConfig.alias + '/cookie-policy'} target='_blank' icon='linkOut' iconPosition='trailing'>
					{withTranslation('Component.Footer.CookiePolicy')}
				</TextLink>
			</StyledFooter>
		</motion.div>
	);
}

@Injectable()
class FloatersController implements ControllerProtocol {
	public static ASPECT = 4 / 2;

	public width = signal(window.innerWidth);
	public height = signal(window.innerHeight);

	public pos = computed(() => {
		const safeArea = this.safeArea();

		return safeArea;
	});

	whenMount() {
		window.addEventListener('resize', (_) => {
			this.width.value = window.innerWidth;
			this.height.value = window.innerHeight;
		});
	}

	private safeArea(): Boundary {
		const windowHeight = this.height.value;
		const windowWidth = this.width.value;

		let footerHeight = 58;

		let normalizeHeight = windowHeight - 32;
		if (this.height.value < 800 + footerHeight * 2) {
			normalizeHeight = normalizeHeight - footerHeight;
		} else {
			footerHeight = 0;
		}

		let maxHeightConst = 800 / normalizeHeight;
		if (maxHeightConst >= 1) maxHeightConst = 1;

		const maxHeight = normalizeHeight * maxHeightConst;
		const newHeight = maxHeight;
		const newWidth = newHeight * FloatersController.ASPECT;

		let y = (windowHeight - newHeight) / 2 - footerHeight;
		if (y < 16) y = 16;
		const x = (windowWidth - newWidth) / 2;

		return { x, y, width: newWidth, height: newHeight };
	}
}

interface Boundary {
	x: number;
	y: number;
	width: number;
	height: number;
}
